import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { DetailHeader } from "@/components/layout/detail-header";
import { useToast } from "@/contexts/toast-context";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { CreateTestModal } from "@/components/modals/create-test-modal";

interface Test {
  id: string;
  name: string;
  description: string;
  group: string | null;
  isAutomatable: boolean;
  createdAt: string;
  runCount?: number; // Will be calculated from API or added later
}

export function TestsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [tests, setTests] = useState<Test[]>([]);
  const [filteredTests, setFilteredTests] = useState<Test[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

  useEffect(() => {
    loadTests();
  }, []);

  // Filter tests based on search
  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredTests(tests);
    } else {
      const filtered = tests.filter(test => 
        test.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (test.group?.toLowerCase().includes(searchValue.toLowerCase())) ||
        test.description.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredTests(filtered);
    }
  }, [tests, searchValue]);

  const loadTests = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<Test[]>("/test");
      const testsData = Array.isArray(response) ? response : [];
      setTests(testsData);
      setFilteredTests(testsData);
    } catch (error) {
      console.error("Failed to load tests:", error);
      showError("Failed to load tests", "Could not fetch tests. Please try again.");
      setTests([]);
      setFilteredTests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTest = async (testId: string) => {
    const test = tests.find(t => t.id === testId);
    const testName = test?.name || "Test";
    
    try {
      setIsDeleting(testId);
      await api.delete(`/test/${testId}`);
      setTests(tests.filter(t => t.id !== testId));
      setShowDeleteModal(null);
      showSuccess("Test deleted successfully", `"${testName}" has been deleted`);
    } catch (error) {
      console.error("Failed to delete test:", error);
      showError("Failed to delete test", `Could not delete "${testName}". Please try again.`);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleTestCreated = (newTest: Test) => {
    setTests(prevTests => [newTest, ...prevTests]);
    setShowCreateModal(false);
    showSuccess("Test created successfully", `"${newTest.name}" has been created`);
    // Navigate to test details page
    navigate(`/app/tests/${newTest.id}`);
  };

  const handleTestClick = (testId: string) => {
    navigate(`/app/tests/${testId}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Invalid date";
    
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const breadcrumb = [
    { label: "All Tests" }
  ];

  const columns = [
    {
      key: "name" as keyof Test,
      header: "Test Name",
      width: "80",
      render: (test: Test) => (
        <button
          onClick={() => handleTestClick(test.id)}
          className="text-left hover:text-primary transition-colors cursor-pointer"
        >
          <div className="font-medium text-xs">{test.name.slice(0, 100) + (test.name.length > 100 ? "..." : "")}</div>
        </button>
      )
    },
    {
      key: "description" as keyof Test,
      header: "Description",
      width: "auto",
      render: (test: Test) => (
        <button
          onClick={() => handleTestClick(test.id)}
          className="text-left hover:text-primary transition-colors cursor-pointer"
        >
          {test.description && (
            <div className="text-xs text-muted-foreground truncate max-w-xs">
              {test.description}
            </div>
          )}
        </button>
      )
    },
    {
      key: "group" as keyof Test,
      header: "Group",
      width: 40,
      render: (test: Test) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          test.group 
            ? "bg-muted text-muted-foreground" 
            : "text-muted-foreground/50"
        }`}>
          {test.group || "No Group"}
        </span>
      )
    },
    {
      key: "runCount" as keyof Test,
      header: "Runs",
      width: 12,
      render: (test: Test) => (
        <div className="text-xs text-muted-foreground">
          {test.runCount || 0}
        </div>
      )
    },
    {
      key: "createdAt" as keyof Test,
      header: "Created",
      width: 20,
      render: (test: Test) => (
        <div className="text-xs text-muted-foreground">
          {formatDate(test.createdAt)}
        </div>
      )
    },
    {
      key: "actions",
      header: "",
      width: 16,
      className: "text-right",
      render: (test: Test) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteModal(test.id);
          }}
          disabled={isDeleting === test.id}
          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
          title="Delete test"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )
    }
  ];

  const actions = (
    <Button
      onClick={() => setShowCreateModal(true)}
      size="sm"
      className="text-xs"
    >
      <Plus className="w-3 h-3 mr-1" />
      Create Test
    </Button>
  );

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search tests..."
        actions={actions}
      />
      
      <div className="flex-1 overflow-hidden">
        <Table
          data={filteredTests}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No tests found"
          fixedHeader={true}
        />
      </div>

      {/* Create Test Modal */}
      <CreateTestModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTestCreated={handleTestCreated}
      />

      {/* Delete Test Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        onConfirm={() => showDeleteModal && handleDeleteTest(showDeleteModal)}
        title="Delete Test"
        message="Are you sure you want to delete this test? This will also delete all associated runs, expectations, and files. This action cannot be undone."
        confirmText="Delete Test"
        type="danger"
        isLoading={isDeleting === showDeleteModal}
      />
    </div>
  );
}
